import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { isSavingAtom } from '@/atoms/_editorAtoms'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { useEditorTurnOverPageSet } from '@/hooks/editor/useEditorTurnOverPageSet'

type EditorTextBoxTurnoverModalProps = {
  show: boolean
  onClose: () => void
}

/**
 * The confirmation before a page set turns over to Lotus text boxes: every
 * TinyMCE text box on every page converts, the editor has no way back, and
 * the edits not saved yet are saved with it. Confirm closes it and runs the
 * turnover, and waits while another Save is writing the line; Cancel, the
 * close control or a click outside runs nothing. It stays mounted while closed, because the turnover
 * it runs raises its toast once the reloaded editor is ready. It is an
 * editor panel (`data-editor-ui`), so a click on it keeps the selection.
 */
export const EditorTextBoxTurnoverModal = ({
  show,
  onClose,
}: EditorTextBoxTurnoverModalProps) => {
  const { t } = useTranslation()
  const isSaving = useRecoilValue(isSavingAtom)
  const turnOverPageSet = useEditorTurnOverPageSet()

  if (!show) return null

  const confirm = () => {
    onClose()
    void turnOverPageSet()
  }

  return createPortal(
    <FullscreenModal
      show={[true, onClose]}
      hideClose={false}
      data-editor-ui="editor-text-box-turnover-confirmation"
    >
      <Flex
        column
        full
        padding="32px"
        maxWidth="600px"
        data-testid="editor-text-box-turnover-modal"
      >
        <Text size="20px" weight="600" margin="10px 0 10px 0">
          {t('editor.textBox.turnover.confirm.title')}
        </Text>
        <Text size="16px" margin="10px 0 10px 0">
          {t('editor.textBox.turnover.confirm.message')}
        </Text>
        <Flex justify="flex-end" align="center" margin="10px 0 0 0">
          <Button
            margin="0 16px 0 0"
            onClick={onClose}
            data-testid="editor-text-box-turnover-cancel"
          >
            {t('editor.textBox.turnover.confirm.cancel')}
          </Button>
          <Button
            primary
            margin="0 16px 0 0"
            onClick={confirm}
            disabled={isSaving}
            data-testid="editor-text-box-turnover-confirm"
          >
            {t('editor.textBox.turnover.confirm.confirm')}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>,
    document.body,
  )
}
